import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_admissions-roadmap/artifacts/2ac8oce6_Mindways%20logo.png";
const CALENDLY_URL = "https://cal.com/mindways/discovery-call";

const navLinks = [
  { label: "Counselling", href: "#counselling" },
  { label: "What We Do", href: "#services" },
  { label: "Tracks", href: "#tracks" },
  { label: "Destinations", href: "#destinations" },
  { label: "Why Mindways", href: "#why-mindways" },
  { label: "FAQs", href: "#faq" },
];

export default function Navbar({ onBookCall }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const openCalendly = () => window.open(CALENDLY_URL, "_blank");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: scrolled ? "rgba(43,47,158,0.97)" : "#2B2F9E",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.15)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "110px" }} className="navbar-inner">
          {/* Logo */}
          <a href="#" onClick={(e) => handleNav(e, "#hero")} data-testid="navbar-logo" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <div style={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", width: "175px", height: "90px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <img src={LOGO} alt="Mindways" className="navbar-logo-img" style={{ display: "block", maxWidth: "none" }} />
            </div>
          </a>

          {/* Desktop nav */}
          <nav style={{ alignItems: "center", gap: "8px" }} className="hidden md:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => handleNav(e, link.href)}
                style={{
                  color: "rgba(255,255,255,0.85)",
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: "500",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  transition: "all 0.2s",
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onMouseEnter={(e) => { e.target.style.color = "#fff"; e.target.style.backgroundColor = "rgba(255,255,255,0.1)"; }}
                onMouseLeave={(e) => { e.target.style.color = "rgba(255,255,255,0.85)"; e.target.style.backgroundColor = "transparent"; }}
              >
                {link.label}
              </a>
            ))}
            <button
              data-testid="navbar-book-call-btn"
              onClick={openCalendly}
              style={{
                backgroundColor: "#F5A000",
                color: "#fff",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
                marginLeft: "8px",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 4px 12px rgba(245,160,0,0.35)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = "#d98e00"; e.target.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = "#F5A000"; e.target.style.transform = "translateY(0)"; }}
            >
              Book a Strategy Call
            </button>
          </nav>

          {/* Mobile hamburger */}
          <button
            data-testid="navbar-mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: "#fff", background: "none", border: "none", cursor: "pointer", padding: "8px" }}
            className="flex md:hidden"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            style={{ backgroundColor: "#1e227a", borderTop: "1px solid rgba(255,255,255,0.1)" }}
          >
            <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: "4px" }}>
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={(e) => handleNav(e, link.href)}
                  style={{
                    color: "rgba(255,255,255,0.9)",
                    textDecoration: "none",
                    fontSize: "16px",
                    fontWeight: "500",
                    padding: "12px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {link.label}
                </a>
              ))}
              <button
                data-testid="mobile-book-call-btn"
                onClick={() => { setMobileOpen(false); openCalendly(); }}
                style={{
                  backgroundColor: "#F5A000",
                  color: "#fff",
                  border: "none",
                  padding: "14px",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "700",
                  cursor: "pointer",
                  marginTop: "12px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Book a Strategy Call
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
